import React, { useEffect, useRef } from 'react';
import { Chart, registerables } from 'chart.js';
import '../styles/ElevationChart.css';

Chart.register(...registerables);

const ElevationChart = ({ coordinates, mapRef }) => {
  const chartRef = useRef(null);
  const chartInstance = useRef(null);
  const isChartCreated = useRef(false); // ← NOU: Flag per evitar recreació
  const coordinatesRef = useRef(coordinates); // ← NOU: Guardem coordenades

  useEffect(() => {
    // Si ja s'ha creat i les coordenades són les mateixes, NO fer res
    if (isChartCreated.current && coordinatesRef.current === coordinates) {
      return;
    }

    if (!coordinates || coordinates.length === 0) return;

    const ctx = chartRef.current?.getContext('2d');
    if (!ctx) return;

    // Actualitza la referència de coordenades
    coordinatesRef.current = coordinates;

    console.log('🎨 Creant gràfica d\'elevació...');

    // Destrueix gràfica anterior SI existeix
    if (chartInstance.current) {
      console.log('🗑️ Destruint gràfica anterior...');
      chartInstance.current.destroy();
      chartInstance.current = null;
    }

    // Calcula distàncies acumulades i pendents
    const data = [];
    let totalDistance = 0;
    
    for (let i = 0; i < coordinates.length; i++) {
      const point = coordinates[i];
      
      if (i > 0) {
        const prev = coordinates[i - 1];
        const R = 6371000; // metres
        const dLat = (point.lat - prev.lat) * Math.PI / 180;
        const dLon = (point.lon - prev.lon) * Math.PI / 180;
        const a = Math.sin(dLat/2) * Math.sin(dLat/2) +
                  Math.cos(prev.lat * Math.PI / 180) * Math.cos(point.lat * Math.PI / 180) *
                  Math.sin(dLon/2) * Math.sin(dLon/2);
        const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
        totalDistance += R * c;
      }
      
      // Calcula pendent (%) amb finestra de 5 punts
      let grade = 0;
      if (i >= 5 && i < coordinates.length - 5) {
        const window = 5;
        const elevDiff = coordinates[i + window].ele - coordinates[i - window].ele;
        let distDiff = 0;
        for (let j = i - window; j < i + window; j++) {
          const p1 = coordinates[j];
          const p2 = coordinates[j + 1];
          const R = 6371000;
          const dLat = (p2.lat - p1.lat) * Math.PI / 180;
          const dLon = (p2.lon - p1.lon) * Math.PI / 180;
          const a = Math.sin(dLat/2) * Math.sin(dLat/2) +
                    Math.cos(p1.lat * Math.PI / 180) * Math.cos(p2.lat * Math.PI / 180) *
                    Math.sin(dLon/2) * Math.sin(dLon/2);
          const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
          distDiff += R * c;
        }
        if (distDiff > 0) {
          grade = (elevDiff / distDiff) * 100;
        }
      }
      
      data.push({
        distance: totalDistance / 1000,
        elevation: point.ele || 0,
        grade: grade
      });
    }

    // Crea nova gràfica
    chartInstance.current = new Chart(ctx, {
      type: 'line',
      data: {
        labels: data.map(d => d.distance.toFixed(1)),
        datasets: [{
          label: 'Elevació (m)',
          data: data.map(d => d.elevation),
          borderColor: 'rgba(102, 126, 234, 1)',
          backgroundColor: (context) => {
            const ctx = context.chart.ctx;
            const gradient = ctx.createLinearGradient(0, 0, 0, 300);
            gradient.addColorStop(0, 'rgba(102, 126, 234, 0.5)');
            gradient.addColorStop(1, 'rgba(102, 126, 234, 0.1)');
            return gradient;
          },
          borderWidth: 2,
          fill: true,
          tension: 0.4,
          pointRadius: 0,
          pointHoverRadius: 6,
          pointHoverBackgroundColor: '#667eea',
          pointHoverBorderColor: 'white',
          pointHoverBorderWidth: 2,
          segment: {
            borderColor: (ctx) => {
              const grade = data[ctx.p0DataIndex]?.grade || 0;
              if (grade > 10) return '#dc2626';
              if (grade > 7) return '#ea580c';
              if (grade > 5) return '#f59e0b';
              if (grade > 3) return '#fbbf24';
              if (grade < -7) return '#059669';
              if (grade < -5) return '#10b981';
              return '#667eea';
            },
            borderWidth: (ctx) => {
              const grade = Math.abs(data[ctx.p0DataIndex]?.grade || 0);
              return grade > 7 ? 3 : 2;
            }
          }
        }]
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        interaction: {
          mode: 'index',
          intersect: false,
        },
        onHover: (event, activeElements) => {
          // NO causar re-renders, actualitzar mapa directament
          if (activeElements.length > 0 && mapRef?.current?.updateHoverPosition) {
            const index = activeElements[0].index;
            const km = data[index].distance;
            mapRef.current.updateHoverPosition(km, coordinatesRef.current);
          } else if (mapRef?.current?.updateHoverPosition) {
            mapRef.current.updateHoverPosition(null);
          }
        },
        plugins: {
          legend: {
            display: false
          },
          tooltip: {
            callbacks: {
              title: (context) => {
                const index = context[0].dataIndex;
                return `KM ${data[index].distance.toFixed(1)}`;
              },
              label: (context) => {
                const index = context.dataIndex;
                const elev = data[index].elevation.toFixed(0);
                const grade = data[index].grade.toFixed(1);
                return [
                  `Elevació: ${elev}m`,
                  `Pendent: ${grade}%`
                ];
              }
            },
            backgroundColor: 'rgba(0, 0, 0, 0.8)',
            padding: 12,
            cornerRadius: 8,
            displayColors: false
          }
        },
        scales: {
          x: {
            title: {
              display: true,
              text: 'Distància (km)',
              font: { size: 12, weight: 'bold' }
            },
            grid: {
              color: 'rgba(0, 0, 0, 0.05)'
            }
          },
          y: {
            title: {
              display: true,
              text: 'Altitud (m)',
              font: { size: 12, weight: 'bold' }
            },
            grid: {
              color: 'rgba(0, 0, 0, 0.05)'
            }
          }
        }
      }
    });

    // Marca com a creat
    isChartCreated.current = true;
    console.log('✅ Gràfica creada!');

    return () => {
      if (chartInstance.current) {
        console.log('🧹 Cleanup: destruint gràfica');
        chartInstance.current.destroy();
        chartInstance.current = null;
        isChartCreated.current = false;
      }
    };
  }, [coordinates, mapRef]);

  return (
    <div className="elevation-chart-container">
      <canvas ref={chartRef}></canvas>
      
      <div className="gradient-legend">
        <span className="legend-item">
          <span className="legend-color" style={{background: '#dc2626'}}></span>
          &gt;10% Pujada
        </span>
        <span className="legend-item">
          <span className="legend-color" style={{background: '#f59e0b'}}></span>
          5-10%
        </span>
        <span className="legend-item">
          <span className="legend-color" style={{background: '#667eea'}}></span>
          Pla
        </span>
        <span className="legend-item">
          <span className="legend-color" style={{background: '#10b981'}}></span>
          Baixada
        </span>
      </div>
    </div>
  );
};

export default ElevationChart;